import {Record, List, Map} from 'immutable';

import {hasValue} from '@famly/stat_ts-utils_has-value';

import * as ListsHelpers from '../../helpers';
import * as Constants from './constants';
import {type ColumnType} from './types';

export type FilterType = 'selection' | 'dropdowns' | 'none';

interface FilterChild {
    id: string;
    name: string;
    filter: FilterType;
    values: any[];
    max: number;
    column: boolean;
    type: ColumnType;
    section?: string;
}

interface SectionFromAPI {
    id: string;
    name: string;
}

interface RawDescription {
    children: FilterChild[];
    sections: SectionFromAPI[];
    defaultColumns: string[];
    defaultFilters: {[key: string]: string};
}

export class EntityValue extends Record<{
    readonly id: string;
    readonly name: string;
    readonly parent: string;
    readonly values: List<{
        id: string;
        name: string;
    }>;
}>({
    id: '',
    name: '',
    parent: '',
    values: List(),
}) {}

export class FilterEntity extends Record<{
    readonly id: string;
    readonly type: string;
    readonly sortable: boolean;
    readonly filter: FilterType;
    readonly values: List<EntityValue>;
    readonly max: number;
    readonly name: string;
    readonly column: boolean;
}>({
    id: '',
    type: '',
    sortable: false,
    filter: 'none' as const,
    values: List(),
    max: 0,
    name: '',
    column: false,
}) {}

const filterEntityFromChild = (child: FilterChild) => {
    return new FilterEntity({
        ...child,
        values: List(child.values.map(value => new EntityValue({...value, values: List(value.values)}))),
    });
};

export class FilterSection extends Record<{
    readonly id: string;
    readonly name: string;
    readonly entities: List<FilterEntity>;
}>({
    id: '',
    name: '',
    entities: List(),
}) {}

export class Column extends Record<{
    readonly id: string;
    readonly name: string;
    readonly sortable: boolean;
    readonly section: string;
    readonly type: ColumnType;
    readonly description?: string;
}>({
    id: '',
    name: '',
    sortable: false,
    section: '',
    type: 'string' as const,
    description: undefined,
}) {}

class ListDescription extends Record<{
    readonly filterSections: Map<string, FilterSection>;
    readonly columnSections: Map<string, List<Column>>;
    readonly defaultFilters: Map<string, string>;
    readonly defaultColumns: List<string>;
}>({
    filterSections: Map(),
    columnSections: Map(),
    defaultFilters: Map(),
    defaultColumns: List(),
}) {}

const addGenderFilter = (filterSections: ListDescription['filterSections'], child: FilterChild) => {
    return filterSections.set(
        child.id,
        new FilterSection({
            id: child.id,
            name: child.name,
            entities: List([
                new FilterEntity({
                    id: child.id,
                    filter: child.filter,
                    name: child.name,
                    values: List(
                        child.values.map(
                            value => new EntityValue({id: value, name: ListsHelpers.getGenderName(value)}),
                        ),
                    ),
                }),
            ]),
        }),
    );
};

const addFilterToSection = (
    filterSections: ListDescription['filterSections'],
    sectionsFromAPI: SectionFromAPI[],
    sectionName: string,
    child: FilterChild,
) => {
    const apiDefinedSection = sectionsFromAPI.find(section => section.id === sectionName);
    const insertedSection = filterSections.find(section => section.id === sectionName);

    if (!apiDefinedSection) {
        return filterSections;
    }

    if (!insertedSection) {
        return filterSections.set(
            apiDefinedSection.id,
            new FilterSection({...apiDefinedSection, entities: List([filterEntityFromChild(child)])}),
        );
    }

    const filterSection = filterSections.get(insertedSection.id);

    if (!filterSection) {
        return filterSections;
    }

    const updatedFilterSection = filterSection.update('entities', entities =>
        entities.push(filterEntityFromChild(child)),
    );

    return filterSections.set(insertedSection.id, updatedFilterSection);
};

const addSection = (filterSections: ListDescription['filterSections'], child: FilterChild) => {
    return filterSections.set(
        child.id,
        new FilterSection({
            id: child.id,
            name: child.name,
            entities: List([filterEntityFromChild(child)]),
        }),
    );
};

const isGenderFilter = (child: FilterChild) => child.filter === 'selection' && child.id === 'gender';

const filterSectionsFromApi = (data: RawDescription) => {
    return data.children
        .filter(child => hasValue(child.filter))
        .reduce<ListDescription['filterSections']>((filterSections, child) => {
            if (isGenderFilter(child)) {
                return addGenderFilter(filterSections, child);
            }

            const sectionName = child.section;
            return sectionName
                ? addFilterToSection(filterSections, data.sections, sectionName, child)
                : addSection(filterSections, child);
        }, Map());
};

const columnSectionsFromApi = (data: RawDescription) => {
    return data.children
        .filter(child => child.column)
        .reduce<Map<string, List<Column>>>((columnSections, child) => {
            const columnSectionName =
                data.sections.find(section => section.id === child.section)?.name ?? Constants.UNDEFINED_COLUMN_SECTION;

            return columnSections.update(columnSectionName, (columns = List()) => {
                return columns.push(new Column(child));
            });
        }, Map());
};

const fromAPI = (data: RawDescription) => {
    return new ListDescription({
        filterSections: filterSectionsFromApi(data),
        columnSections: columnSectionsFromApi(data),
        defaultColumns: List(data.defaultColumns),
        defaultFilters: Map(data.defaultFilters),
    });
};

export const FiltersAndColumns = {
    fromAPI,
    default: new ListDescription(),
};
